import { DayOfWeek } from '../../types/day-of-week';
import { CurrencyCode } from './enum/currency-code';
import { DiscountStatus } from './enum/discount-status';
import { DiscountType } from './enum/discount-type';
import { HoursAvailable } from './hours-available';
import { IStore } from './store';

export interface IDiscountSummary {
  id: number;
  code: string;
  createdAt: Date;
  updatedAt: Date;
  type: DiscountType;
  formattedValue: number;
  status: DiscountStatus;
  redemptionsCount: number;
  currencyCode: CurrencyCode; // relevant only if DiscountType is FIXED
}

export interface IDiscountDetail extends IDiscountSummary {
  description: string;
  applicableStores: IStore['id'][];
  isArchived: boolean;
  validFrom: Date | null; // null means discount will be valid from createdAt
  validThrough: Date | null; // null means discount will be valid indefinitely
  daysAvailable: DayOfWeek[];
  isAvailableAllDay: boolean;
  hoursAvailable?: HoursAvailable | null; // relevant only if isAvailableAllDay is false
  minimumSpend: number;
  redemptionLimit: number;
  redemptionLimitPerCustomer: number;
}

export interface IDiscountCreateData {
  code: string;
  description?: string;
  type: DiscountType;
  value: number;
  currencyCode?: CurrencyCode; // relevant only if DiscountType is FIXED
  storeIds: IStore['id'][];
  validFrom?: string | null;
  validThrough?: string | null;
  daysAvailable?: DayOfWeek[];
  isAvailableAllDay: boolean;
  hoursAvailable?: HoursAvailable | null;
  minimumSpend?: number;
  limitOverall?: number;
  limitCustomer?: number;
}

export interface IDiscountUpdateData {
  code?: string;
  description?: string;
  type?: DiscountType;
  value?: number;
  currencyCode?: CurrencyCode; // relevant only if DiscountType is FIXED
  storeIds?: IStore['id'][];
  validFrom?: string | null;
  validThrough?: string | null;
  daysAvailable?: DayOfWeek[];
  isAvailableAllDay?: boolean;
  hoursAvailable?: HoursAvailable | null;
  minimumSpend?: number;
  limitOverall?: number;
  limitCustomer?: number;
  isArchived?: boolean;
}

export class DiscountSummary implements IDiscountSummary {
  constructor(
    public readonly id: number = 0,
    public readonly code: string = '',
    public readonly createdAt: Date = new Date(),
    public readonly updatedAt: Date = new Date(),
    public readonly type: DiscountType = DiscountType.PERCENTAGE,
    public readonly formattedValue: number = 0,
    public readonly status: DiscountStatus = DiscountStatus.ACTIVE,
    public readonly redemptionsCount: number = 0,
    public readonly currencyCode: CurrencyCode = CurrencyCode.PHP
  ) {}

  static fromJSON(json: any) {
    return new DiscountSummary(
      json.id,
      json.code,
      new Date(json.createdAt),
      new Date(json.updatedAt),
      json.type,
      +json.value,
      json.status,
      +json.redemptionsCount,
      json.currencyCode
    );
  }
}

export class DiscountDetail implements IDiscountDetail {
  constructor(
    public readonly id: number = 0,
    public readonly code: string = '',
    public readonly description: string = '',
    public readonly createdAt: Date = new Date(),
    public readonly updatedAt: Date = new Date(),
    public readonly type: DiscountType = DiscountType.PERCENTAGE,
    public readonly value: number = 0,
    public readonly currencyCode: CurrencyCode = CurrencyCode.PHP, // relevant only if DiscountType is FIXED
    public readonly applicableStores: IStore['id'][] = [],
    public readonly isArchived: boolean = false,
    public readonly validFrom: Date | null = null, // null means discount will be valid from createdAt
    public readonly validThrough: Date | null = null, // null means discount will be valid indefinitely
    public readonly daysAvailable: DayOfWeek[] = [],
    public readonly isAvailableAllDay: boolean = true,
    public readonly hoursAvailable?: HoursAvailable | null, // relevant only if isAvailableAllDay is false
    public readonly minimumSpend: number = 0,
    public readonly redemptionLimit: number = 0,
    public readonly redemptionLimitPerCustomer: number = 0,
    public readonly status: DiscountStatus = DiscountStatus.ACTIVE,
    public readonly redemptionsCount: number = 0
  ) {}

  get formattedValue() {
    return this.value;
  }

  static fromJSON(json: any) {
    return new DiscountDetail(
      json.id,
      json.code,
      json.description,
      json.createdAt,
      json.updatedAt,
      json.type,
      json.value,
      json.currencyCode, // relevant only if DiscountType is FIXED
      json.applicableStores,
      json.isArchived,
      json.validFrom, // null means discount will be valid from createdAt
      json.validThrough, // null means discount will be valid indefinitely
      json.daysAvailable,
      json.isAvailableAllDay,
      json.hoursAvailable, // relevant only if isAvailableAllDay is false
      json.minimumSpend,
      json.redemptionLimit,
      json.redemptionLimitPerCustomer,
      json.status,
      json.redemptionsCount
    );
  }
}
